import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/common/custom-component/ToastProvider';
import { submitFormioSubmissionThunk } from '../store/thunks/formioThunks';
import { logger } from 'helpers/logger';

// Shared "submit a Form.io submission" core used by both ManageFormsPage's
// Fill Form tab and FormRendererPage's Execute Task flow. Only owns the
// dispatch/toast concern - loading state and what to do after success (tab
// switch vs navigate away) stay with each caller since those differ per page.
export function useSubmitFormioSubmission() {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const { success: ToastSuccess, error: ToastError } = useToast();
  const activeThunksRef = useRef([]);

  useEffect(() => {
    return () => {
      activeThunksRef.current.forEach((thunk) => {
        if (thunk && typeof thunk.abort === 'function') thunk.abort();
      });
      activeThunksRef.current = [];
    };
  }, []);

  const submitSubmission = useCallback(
    async ({ formId, submissionId, processInstanceId, taskId, changedFields }) => {
      try {
        // "submit" is Form.io's own submit-button component key leaking into
        // submission.data when it was clicked - not real form data, so strip
        // it before persisting. changedFields is already dirty-fields-only,
        // courtesy of FormRenderer's useDirtyFieldTracker/getDirtyFields.
        const { submit, ...rawFields } = changedFields || {};

        const thunkPromise = dispatch(
          submitFormioSubmissionThunk({
            formId,
            submissionId,
            submissionData: rawFields,
            processInstanceId,
            taskId,
          })
        );
        activeThunksRef.current.push(thunkPromise);

        const result = await thunkPromise;
        activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

        if (submitFormioSubmissionThunk.fulfilled.match(result)) {
          ToastSuccess(t('Form submitted successfully!'));
          return true;
        }

        if (submitFormioSubmissionThunk.rejected.match(result)) {
          const msg = result.payload || t('An error occured, please try again later.');
          ToastError(msg, { duration: null });
        }
        return false;
      } catch (e) {
        logger.error('Unexpected error in submitSubmission:', e);
        ToastError(t('An unexpected error occurred'), { duration: null });
        return false;
      }
    },
    [dispatch, ToastSuccess, ToastError, t]
  );

  return { submitSubmission };
}
